
import { useMutation, useQueryClient, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { axiosReq } from "@/lib/axios";
import { LoadingToast } from "../components/LoadingToast";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { ChevronRight } from "lucide-react";

interface CreateImagePostArgs {
  image_id: number;
  description: string;
}

interface CreateImagePostResponse {
  data: string;
}

export const useCreateVideo = () => {

  const navigate = useNavigate()

  const queryClient = useQueryClient();

  return useMutation<CreateImagePostResponse, Error, CreateImagePostArgs>({
    mutationFn: async (payload) => {
      const { data } = await axiosReq.post("/api/asset/create-image-video/", payload);
      return data;
    },
    onSuccess: ({ data: jobId }) => {
      // Create a toast UI for the job
      const toastId = toast.custom(() => (
        <LoadingToast />
      ), { duration: Infinity });

      // Start background polling (even across pages)
      startJobWatcher(jobId, toastId, queryClient, () => navigate('/home/gallery?view=videos'));
    },
    onError: (error) => {
      toast.error(`Failed to start job: ${error.message}`);
    },
  });
};

interface JobStatusResponse {
  status: string;
}

const activeJobs = new Map<string, NodeJS.Timeout>(); // jobId -> polling interval

export const startJobWatcher = (
  jobId: string,
  toastId: string | number,
  queryClient: QueryClient,
  onGenerate: () => void
) => {
  // Prevent duplicate polling for same job
  if (activeJobs.has(jobId)) return;

  const poll = async () => {
    try {
      const { data } = await axiosReq.post<JobStatusResponse>(`/api/asset/generation-job-status/`, { task_id: jobId });

      // Cache in React Query for consistency (optional)
      queryClient.setQueryData(["jobStatus", jobId], data);

      if (data.status === "SUCCESS") {
        toast.dismiss(toastId);
        toast.success(`Video generated successfully!`, { duration: 30_000, action: (
          <Button 
            size="icon"
            variant="outline"
            className="rounded-full size-7 border border-purple-500 ms-1"
          onClick={onGenerate}> 
            <ChevronRight />
          </Button> ) });
        queryClient.invalidateQueries({ queryKey: ["video-catalogue"] })
        clearInterval(activeJobs.get(jobId));
        activeJobs.delete(jobId);
      } else if (data.status === "FAILED") {
        toast.dismiss(toastId);
        toast.error(`Something went wrong, try again`);
        clearInterval(activeJobs.get(jobId));
        activeJobs.delete(jobId);
      }
    } catch (err) {
      console.error(`Polling error for job ${jobId}`, err);
    }
  };

  // Start immediately, then every minute
  poll();
  const interval = setInterval(poll, 60_000);
  activeJobs.set(jobId, interval);
};

